import { useBlocker, useNavigate, useSearch } from '@tanstack/react-router';
import { Columns3, Copy } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ModuleState, RestrictedTransition } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Popover, usePopover } from '../../../components/Popover';
import { SaveBar } from '../../../components/SaveBar';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { useModules } from '../../../lib/modules';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { isStaleWrite, useReloadRole, useRole, useRoles, useUpdateRole, type RoleCell, type RoleDetail } from '../api/roles-api';
import { CompareRoles } from '../components/roles/CompareRoles';
import { RoleExtras, RolePlainWords } from '../components/roles/RoleAside';
import { RoleHeader } from '../components/roles/RoleHeader';
import { RoleList, RolePicker, listedRoles, peopleText } from '../components/roles/RoleList';
import { RoleRows } from '../components/roles/RoleRows';
import {
  buildUpdate,
  changeCount,
  changeLines,
  draftOf,
  hiddenCount,
  plainWords,
  visibleAreas,
  visibleTransitions,
  type RoleDraft,
} from '../components/roles/role-editor-model';
import { NewRoleForm } from './NewRoleForm';
import styles from './RoleBuilderPage.module.css';

/**
 * USER-04 — Roles (Admin redesign 2026-11-02, Roles A). F2.04, F2.08, F2.09, F2.10.
 *
 * The roles on the left, the editor in the middle, and on the right the role in plain words as it
 * is changed. Each row asks two questions, "Which ones can they open?" and "What can they do?",
 * instead of four levels and a "Read only" box. A role changes many people's access at once, so
 * this page keeps Save (and Discard) rather than saving each click.
 */

/** Laptop width and below: the role list becomes a drop-down above the editor, and the summary moves under it. */
const COMPACT_MEDIA = '(max-width: 1180px)';
const COPY_WIDTH = 360;
const DISCARD_WIDTH = 300;

export function RoleBuilderPage(): ReactNode {
  const canView = useCanView('user_management');
  const canEdit = useCanEdit('user_management');
  const modules = useModules();
  const isCompact = useMediaQuery(COMPACT_MEDIA);
  // Review R22: only for someone who may open Roles; anyone else's visit would ask for `/roles` and be refused.
  const roles = useRoles(canView);
  const listed = listedRoles(roles.data ?? []);
  // The open role is in the address (`?role=<id>`), so a link (the Access log's) opens it directly.
  const search = useSearch({ from: '/app-layout/admin/roles' });
  const navigate = useNavigate();
  const setSelectedId = (id: string | null): void => {
    void navigate({ to: '/admin/roles', search: id === null ? {} : { role: id }, replace: true });
  };
  const [showHidden, setShowHidden] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const { anchor: copyAnchor, triggerRef: copyTriggerRef, isOpen: isCopyOpen, toggle: toggleCopy, close: closeCopy } = usePopover();

  const selected = search.role ?? listed[0]?.id ?? null;
  const role = useRole(selected);

  // Review R17 (06-qol §4): changes not yet saved are never thrown away without asking. Leaving the app warns; opening
  // another role, here or by finishing "Copy a role", asks beside the role list first.
  const [unsaved, setUnsaved] = useState(0);
  useUnsavedWarning(unsaved > 0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [discard, setDiscard] = useState<{ readonly id: string; readonly anchor: AnchorRect } | null>(null);
  const pick = (id: string): void => {
    if (id === selected) return;
    if (unsaved > 0 && listRef.current) {
      // Beside what was used to ask: the role clicked, or "Copy a role" once its form closes; else the list.
      setDiscard({ id, anchor: anchorFrom(focusedOr(listRef.current)) });
      return;
    }
    setSelectedId(id);
  };
  const confirmDiscard = (): void => {
    if (discard === null) return;
    setDiscard(null);
    setSelectedId(discard.id);
  };

  // Review R17, finished: moving to another page inside the app (the Admin menu, a link) asks the same question,
  // beside what was clicked, before the changes go. Picking a role stays on this page and asks above.
  const unsavedRef = useRef(unsaved);
  useEffect(() => {
    unsavedRef.current = unsaved;
  }, [unsaved]);
  const [leaveAnchor, setLeaveAnchor] = useState<AnchorRect | null>(null);
  const leaving = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      const isBlocked = unsavedRef.current > 0 && current.pathname !== next.pathname;
      if (isBlocked) setLeaveAnchor(anchorFrom(focusedOr(listRef.current)));
      return isBlocked;
    },
    // The tab closing or reloading is the browser's own warning (`useUnsavedWarning`), not this one.
    enableBeforeUnload: false,
    withResolver: true,
  });
  const stayHere = (): void => {
    setLeaveAnchor(null);
    leaving.reset?.();
  };
  const leaveAnyway = (): void => {
    setLeaveAnchor(null);
    leaving.proceed?.();
  };

  if (!canView) {
    return <ForbiddenState resource="managing roles and people" contact="Your admin" />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Roles <span className={styles.count}>{listed.length}</span>
        </h1>
        <div className={styles.headActions}>
          <Button variant="outline" icon={Columns3} aria-haspopup="dialog" onClick={() => setIsComparing(true)}>
            Compare all roles
          </Button>
          {canEdit && (
            <Button ref={copyTriggerRef} variant="solid" icon={Copy} aria-haspopup="dialog" aria-expanded={isCopyOpen} onClick={toggleCopy}>
              Copy a role
            </Button>
          )}
        </div>
      </header>
      <p className={styles.lede}>
        A role decides what someone can open and change. Pick a role, answer two plain questions for each thing, and check the result in plain words.
      </p>

      <div className={styles.layout}>
        <div ref={listRef}>
          {isCompact ? (
            <RolePicker roles={listed} selectedId={selected} onSelect={pick} />
          ) : (
            <RoleList roles={listed} isLoading={roles.isPending} selectedId={selected} onSelect={pick} />
          )}
        </div>

        {role.data ? (
          // Keyed on the version as well as the id: a save bumps the version, so the editor starts again
          // from what was saved instead of an effect writing state over whatever is on screen.
          <RoleEditor
            key={`${role.data.id}:${role.data.version}`}
            role={role.data}
            canEdit={canEdit}
            modules={modules}
            showHidden={showHidden}
            onShowHiddenChange={setShowHidden}
            onUnsavedChange={setUnsaved}
            onDeleted={() => setSelectedId(null)}
          />
        ) : role.isError ? (
          <div className={styles.editor}>
            <EmptyState
              isInset
              title="This role no longer exists"
              body="It may have been deleted. Pick another role from the list."
              action={
                <Button variant="outline" size="sm" onClick={() => setSelectedId(null)}>
                  Open the first role
                </Button>
              }
            />
          </div>
        ) : selected !== null ? (
          <div className={styles.editor}>
            <SkeletonLines count={10} />
          </div>
        ) : null}
      </div>

      <Popover anchor={copyAnchor} onClose={closeCopy} align="right" width={COPY_WIDTH} label="Copy a role">
        <NewRoleForm
          roles={listed}
          onCancel={closeCopy}
          onCreated={(id) => {
            closeCopy();
            pick(id);
          }}
        />
      </Popover>

      <Popover anchor={discard?.anchor ?? null} onClose={() => setDiscard(null)} width={DISCARD_WIDTH} label="Discard changes">
        <div className={styles.discard}>
          <h3 className={styles.discardTitle}>
            Discard {unsaved} {unsaved === 1 ? 'change' : 'changes'} to {role.data?.label ?? 'this role'}?
          </h3>
          <p className={styles.discardBody}>They haven't been saved. Keep editing to save them first.</p>
          <div className={styles.discardActions}>
            <Button variant="ghost" size="sm" onClick={() => setDiscard(null)}>
              Keep editing
            </Button>
            <Button variant="dangerSolid" size="sm" autoFocus onClick={confirmDiscard}>
              Discard
            </Button>
          </div>
        </div>
      </Popover>

      <Popover anchor={leaving.status === 'blocked' ? leaveAnchor : null} onClose={stayHere} width={DISCARD_WIDTH} label="Leave without saving">
        <div className={styles.discard}>
          <h3 className={styles.discardTitle}>
            Leave and discard {unsaved} {unsaved === 1 ? 'change' : 'changes'} to {role.data?.label ?? 'this role'}?
          </h3>
          <p className={styles.discardBody}>They haven't been saved. Keep editing to save them first.</p>
          <div className={styles.discardActions}>
            <Button variant="ghost" size="sm" onClick={stayHere}>
              Keep editing
            </Button>
            <Button variant="dangerSolid" size="sm" autoFocus onClick={leaveAnyway}>
              Discard and leave
            </Button>
          </div>
        </div>
      </Popover>

      <CompareRoles isOpen={isComparing} onClose={() => setIsComparing(false)} modules={modules} showHidden={showHidden} />
    </div>
  );
}

/** What the person used to ask, so the question opens beside it; the role list when nothing has focus. */
function focusedOr(fallback: HTMLElement | null): Element {
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && focused !== document.body) return focused;
  return fallback ?? document.body;
}

// --- The editor --------------------------------------------------------------------------------

interface RoleEditorProps {
  readonly role: RoleDetail;
  readonly canEdit: boolean;
  readonly modules: ModuleState;
  readonly showHidden: boolean;
  readonly onShowHiddenChange: (show: boolean) => void;
  /** How many changes are not saved yet, so the page can ask before they are lost (review R17). */
  readonly onUnsavedChange: (count: number) => void;
  readonly onDeleted: () => void;
}

function RoleEditor({ role, canEdit, modules, showHidden, onShowHiddenChange, onUnsavedChange, onDeleted }: RoleEditorProps): ReactNode {
  const update = useUpdateRole(role.id);
  const reload = useReloadRole(role.id);
  const showToast = useToasts((state) => state.show);
  const [draft, setDraft] = useState<RoleDraft>(() => draftOf(role));
  const [isStale, setIsStale] = useState(false);

  const areas = visibleAreas(modules, showHidden);
  const transitions = visibleTransitions(modules, showHidden);
  const count = changeCount(role, draft);
  const people = role.assignedUserCount;
  useEffect(() => {
    onUnsavedChange(count);
    return () => onUnsavedChange(0);
  }, [count, onUnsavedChange]);

  const setCell = (cell: RoleCell): void => setDraft((current) => ({ ...current, cells: { ...current.cells, [cell.objectKey]: cell } }));
  const setTransition = (key: RestrictedTransition, isAllowed: boolean): void =>
    setDraft((current) => ({ ...current, transitions: { ...current.transitions, [key]: isAllowed } }));

  const save = (): void => {
    const payload = buildUpdate(role, draft);
    if (payload === null) return;
    update.mutate(payload, {
      onSuccess: () => showToast({ tone: 'success', message: `${draft.label.trim()} saved` }),
      onError: (error) => {
        if (isStaleWrite(error)) setIsStale(true);
        else showToast({ tone: 'danger', message: error.message });
      },
    });
  };

  return (
    <>
      <section className={styles.editor} aria-label={`Editing ${role.label}`}>
        <RoleHeader
          role={role}
          draft={draft}
          canEdit={canEdit}
          onRename={(label, description) => setDraft((current) => ({ ...current, label, description }))}
          onDeleted={onDeleted}
        />
        <RoleRows role={role} draft={draft} areas={areas} modules={modules} canEdit={canEdit} onCellChange={setCell} />
        {canEdit && (
          <div className={styles.saveBar}>
            <SaveBar
              status={
                isStale ? (
                  <span className={styles.stale} role="alert">
                    Someone else saved this role while you were working on it, so your changes were not saved. Reload to see theirs; your changes here will
                    be lost.
                  </span>
                ) : count === 0 ? (
                  'No changes yet'
                ) : (
                  <strong className={styles.status}>
                    {count} {count === 1 ? 'change' : 'changes'}
                    {people > 0 ? `, for ${peopleText(people)}` : ''}
                  </strong>
                )
              }
            >
              {isStale ? (
                <Button variant="solid" size="sm" onClick={() => void reload()}>
                  Reload the role
                </Button>
              ) : (
                <>
                  <Button variant="ghost" size="sm" disabled={count === 0 || update.isPending} onClick={() => setDraft(draftOf(role))}>
                    Discard
                  </Button>
                  <Button variant="solid" size="sm" disabled={count === 0} isPending={update.isPending} onClick={save}>
                    {people > 0 ? `Save for ${peopleText(people)}` : 'Save'}
                  </Button>
                </>
              )}
            </SaveBar>
          </div>
        )}
      </section>

      <RolePlainWords
        className={styles.plainWords}
        changes={changeLines(role, draft)}
        sentences={plainWords(
          role,
          draft,
          areas.flatMap((area) => area.objects),
          transitions,
        )}
      />
      <RoleExtras
        className={styles.extras}
        roleId={role.id}
        transitions={transitions}
        allowed={draft.transitions}
        onTransitionChange={setTransition}
        assignedUserCount={people}
        hiddenCount={hiddenCount(modules)}
        showHidden={showHidden}
        onShowHiddenChange={onShowHiddenChange}
        canEdit={canEdit}
      />
    </>
  );
}
